<template>
    <button
        class="pretorButton"
        :class="classObject"
        :disabled="disabled || loading"
        :style="style"
    >
        <!--
            Default slot lets callers compose fully custom content (SVG/IMG + text).
            When no slot content is passed, fall back to the image/icon/label props.
        -->
        <slot>
            <!-- Processing state: the spinner stands in for the icon/image while
                 the label keeps describing what's happening. -->
            <span v-if="loading" class="pretorButton__spinner" aria-hidden="true"></span>
            <template v-else>
                <img
                    v-if="image !== ''"
                    class="pretorButton__image"
                    :src="image"
                    :alt="imageAlt"
                />
                <i v-if="hasIcon" class="pretorButton__icon" :class="icon"></i>
            </template>
            <span v-if="label !== ''" class="pretorButton__label">{{ label }}</span>
        </slot>
    </button>
</template>

<script>

export default {
    name: 'PretorButton',
    props: {
        icon: {
            type: String,
            default: ''
        },
        label: {
            type: String,
            default: ''
        },
        image: {
            type: String,
            required: false,
            default: ''
        },
        imageAlt: {
            type: String,
            required: false,
            default: ''
        },
        variant: {
            type: String,
            required: false,
            default: 'primary'
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false
        },
        // Processing state: swaps the icon/image for a spinner, keeps the label,
        // and force-disables the button so it can't be triggered again.
        loading: {
            type: Boolean,
            required: false,
            default: false
        },
        style: {
            type: String,
            required: false,
            default: ''
        },
        Class: {
            type: String,
            required: false,
            default: ''
        },
    },
    computed: {
        hasIcon() {
            return this.icon !== '';
        },
        classObject() {
            const classes = {};

            if (this.Class !== '') {
                classes[this.Class] = true;
            }

            // Base `.pretorButton` already is the primary style, so only tag
            // non-primary variants (e.g. `pretorButton--outline`).
            if (this.variant && this.variant !== 'primary') {
                classes[`pretorButton--${this.variant}`] = true;
            }

            // Keep the active look while disabled-by-loading, instead of the
            // muted `:disabled` gray.
            if (this.loading) {
                classes['pretorButton--loading'] = true;
            }

            return classes;
        }
    },
}
</script>

<style scoped>
.pretorButton {
    /* Strong ease-out for UI interactions */
    --ease-out: cubic-bezier(0.23, 1, 0.32, 1);

    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 36px;
    padding: 5px 12px;
    background-color: var(--LativoDarkPurple);
    border: none;
    border-radius: 8px;
    color: white;
    font-family: 'SwitzerMedium', sans-serif;
    font-size: 14px;
    text-align: center;
    cursor: pointer;
    outline: none;
    transition:
        background-color 200ms ease,
        filter 120ms ease;
}

/* Press feedback — makes the button feel like it heard you.
   Deliberately NOT a transform: scaling the button itself shrinks its own
   hit area while the mouse button is held, so a press near the edges could
   release outside the (now smaller) box and the click would never fire. */
.pretorButton:active:not(:disabled) {
    filter: brightness(0.88);
}

.pretorButton.gray:active:not(:disabled) {
    filter: none;
    background-color: rgb(190, 190, 190);
}

.pretorButton--outline:active:not(:disabled) {
    filter: none;
    background-color: #f2f0fa;
}

/* Keyboard accessibility without punishing mouse users */
.pretorButton:focus-visible {
    outline: 2px solid var(--LativoDarkPurple);
    outline-offset: 2px;
}

.pretorButton:disabled {
    background-color: rgb(71, 71, 84);
    cursor: not-allowed;
}

.pretorButton__icon {
    font-size: 0.95em;
    line-height: 1;
}

.pretorButton__label {
    line-height: 1.2;
}

.pretorButton__image {
    height: 18px;
    width: auto;
    display: block;
}

/* Processing spinner — a constant-motion loading indicator, so it spins
   `linear` and never stops until the state clears. transform/opacity only,
   GPU-friendly. */
.pretorButton__spinner {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.35);
    border-top-color: #ffffff;
    animation: pretorButtonSpin 700ms linear infinite;
}

@keyframes pretorButtonSpin {
    to { transform: rotate(360deg); }
}

/* Loading look — hold the active purple (not the muted disabled gray) so the
   button reads as "working", and swap the cursor to a progress indicator. */
.pretorButton--loading,
.pretorButton--loading:disabled {
    background-color: var(--LativoDarkPurple);
    color: white;
    cursor: progress;
    opacity: 0.92;
}

/* Reduced motion — keep an indicator, drop the rotation: pulse the opacity
   instead so it's gentler but still shows work in progress. */
@media (prefers-reduced-motion: reduce) {
    .pretorButton__spinner {
        animation: pretorButtonSpinnerPulse 1s ease-in-out infinite;
    }
}

@keyframes pretorButtonSpinnerPulse {
    0%, 100% { opacity: 1; }
    50%      { opacity: 0.4; }
}

/* Gray variant */
.pretorButton.gray {
    background-color: rgb(220, 220, 220);
    color: black;
}

/* Outline variant — light surface with a hairline border (e.g. Google button) */
.pretorButton--outline {
    background-color: #ffffff;
    color: var(--LativoDarkPurple);
    border: 1px solid #dcd8e6;
}

/* Hover only on real pointer devices — avoids sticky tap-hover on touch */
@media (hover: hover) and (pointer: fine) {
    .pretorButton:hover:not(:disabled) {
        filter: brightness(1.22);
    }

    .pretorButton.gray:hover:not(:disabled) {
        filter: none;
        background-color: rgb(200, 200, 200);
    }

    /* Outline uses a surface/border shift instead of brightness */
    .pretorButton--outline:hover:not(:disabled) {
        filter: none;
        background-color: #faf9ff;
        border-color: #c9c3d8;
    }
}
</style>
